import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import StructureEditor from '@Containers/StructureEditor';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import { StructureEditorType } from '@Containers/StructureEditor/constants';
import { withRouter } from 'react-router-dom';
import EditorSwitcher from './EditorSwitcher';
import EditorControls from './EditorControls';
import GSFN from './GSFN';
import RetroSynthesisQueryOptionsPanel from './OptionsPanel';
import { onDrawingCancelled, onDrawingCleared, onEditorTypeSelected } from './analytics';

import './DrawStructure.scss';

let tempData;
/* eslint-disable no-shadow */
/* eslint-disable react-hooks/exhaustive-deps */
function DrawStructure({
  optionResponse,
  vendorsInfo,
  doSubmit,
  hasProjects,
  // eslint-disable-next-line react/prop-types
  history,
  structure,
  initialPredictedParameters,
  initialPublishedParameters,
  editorType,
  setEditorType,
  isLoading,
  setIsLoading,
}) {
  const editorRef = React.useRef(null);
  const [isEmptyEditor, setIsEmptyEditor] = useState(true);
  const [isEditorLoading, setIsEditorLoading] = useState(true);
  const [projectParams, setProjectParams] = useState({});
  const [isValidationError, setValidationError] = useState(false);

  const cancelHandler = () => {
    onDrawingCancelled();
    // eslint-disable-next-line react/prop-types
    history.push(RsRoute.PROJECTS_LIST);
  };

  useEffect(() => {
    if (structure) {
      tempData = structure;
    }
  }, []);

  // eslint-disable-next-line no-shadow
  const insertStructureHandler = (structure) => {
    try {
      const editorInstance = get(editorRef, 'current.instance');
      editorInstance.clear();
      editorInstance.loadStructure(structure);
    } catch (e) {
      Logger.warn('DrawStructure insert structure failed with error:', e);
    }
  };

  const clearHandler = () => {
    try {
      editorRef.current.instance.clear();
    } catch (e) {
      Logger.warn('DrawStructure clear instances failed with error:', e);
    }
  };

  const synthesisHandler = async () => {
    setIsLoading(true);
    try {
      const { data } = await editorRef.current.instance.getData();
      await doSubmit(projectParams, data);
    } catch (e) {
      Logger.error('DrawStructure submit failed with error:', e);
    }
    setIsLoading(false);
  };

  const editorTypeChangeHandler = async (newEditorType) => {
    try {
      onEditorTypeSelected(newEditorType);
      setIsEditorLoading(true);
      try {
        // For some reason MarvinJS returns some data even for empty editor
        const isEditorEmpty = await (editorRef.current && editorRef.current.instance && editorRef.current.instance.isEmpty());
        if (!isEditorEmpty) {
          const { data } = await editorRef.current.instance.getData();
          tempData = data;
        }
      } catch (e) {
        Logger.error('DrawStructure transfer data when switching editor failed with error:', e);
      }
      setEditorType(newEditorType);
    } catch (e) {
      Logger.error('DrawStructure change editor error:', e);
    }
  };

  const loadedEditorHandler = async () => {
    if (tempData) {
      try {
        await editorRef.current.instance.loadStructure(tempData);
        tempData = undefined;
      } catch (e) {
        Logger.error('DrawStructure loadStructure error:', e);
      }
    }
    setIsEditorLoading(false);
  };

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => clearHandler();
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <div className="draw-structure" id="main-content" tabIndex="0">
      <div className="editor">
        <div className="draw-structure__top-panel">
          <EditorSwitcher editorType={editorType} onChange={editorTypeChangeHandler} isLoading={isEditorLoading} />
          <GSFN onStructureLoaded={insertStructureHandler} />
        </div>
        <StructureEditor
          onChange={setIsEmptyEditor}
          editorType={editorType}
          editorInstanceRef={editorRef}
          className="retro-synthesis-query--structure-editor"
          onLoaded={loadedEditorHandler}
        />
        <EditorControls
          onSynthesise={synthesisHandler}
          onCancel={cancelHandler}
          onClear={() => {
            onDrawingCleared();
            clearHandler();
          }}
          isEmptyEditor={isEmptyEditor}
          hasProjects={hasProjects}
          isLoading={isLoading}
          isValidationError={isValidationError}
        />
      </div>
      <RetroSynthesisQueryOptionsPanel
        optionResponse={optionResponse}
        vendorsInfo={vendorsInfo}
        initialPublishedParameters={initialPublishedParameters}
        initialPredictedParameters={initialPredictedParameters}
        onProjectParamsChanged={setProjectParams}
        handleValidationError={setValidationError}
      />
    </div>
  );
}

export default withRouter(DrawStructure);

DrawStructure.defaultProps = {
  setEditorType: PropTypes.func,
  setIsLoading: () => null,
};

DrawStructure.propTypes = {
  optionResponse: PropTypes.array,
  vendorsInfo: PropTypes.array,
  doSubmit: PropTypes.func,
  hasProjects: PropTypes.bool,
  isLoading: PropTypes.bool,
  structure: PropTypes.string,
  initialPublishedParameters: PropTypes.object,
  initialPredictedParameters: PropTypes.object,
  editorType: PropTypes.oneOf(StructureEditorType.allTypes),
  setEditorType: PropTypes.func,
  setIsLoading: PropTypes.func,
};
